import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Icon } from '../lib/icons.js';
import { useHouse } from '../lib/store.js';
import { isStandalone } from '../lib/format.js';
import { useOnline } from '../lib/online.js';

export function AppBar({
  title,
  subtitle,
  back,
  right,
}: {
  title: string;
  subtitle?: string | undefined;
  back?: boolean;
  right?: ReactNode;
}): ReactNode {
  const navigate = useNavigate();
  return (
    <header className="appbar">
      {back && (
        <button type="button" className="icon-btn" aria-label="Back" onClick={() => navigate(-1)}>
          <Icon name="back" size={17} />
        </button>
      )}
      <div style={{ minWidth: 0 }}>
        <h1>{title}</h1>
        {subtitle && <div className="sub">{subtitle}</div>}
      </div>
      <span className="spacer" />
      {right}
    </header>
  );
}

export function ConnectionPill(): ReactNode {
  const { connection } = useHouse();
  const online = useOnline();
  if (connection === 'live') return null;
  const label = !online
    ? 'You are offline — showing the last known state'
    : connection === 'connecting'
      ? 'Connecting to the house…'
      : connection === 'ended'
        ? 'Your access has ended'
        : 'Reconnecting to the house…';
  return (
    <div className="glass conn-banner">
      <Icon name={connection === 'ended' ? 'clock' : 'wifi-off'} size={15} />
      {label}
    </div>
  );
}

export function TabBar(): ReactNode {
  // The tab stays once installed — the wifi password lives on that page, so
  // hiding it took the wifi with it. Only the label changes.
  const installed = isStandalone();
  const { context } = useHouse();
  return (
    <nav className="glass tabbar" aria-label="Main">
      <NavLink to="/" end>
        <Icon name="home" size={17} />
        <span>Home</span>
      </NavLink>
      <NavLink to="/rooms">
        <Icon name="rooms" size={17} />
        <span>Rooms</span>
      </NavLink>
      {context?.hasHouseInfo && (
        <NavLink to="/info">
          <Icon name="info" size={17} />
          <span>Notes</span>
        </NavLink>
      )}
      <NavLink to="/install">
        <Icon name={installed ? 'wifi' : 'download'} size={17} />
        <span>{installed ? 'Connect' : 'Install'}</span>
      </NavLink>
    </nav>
  );
}
